export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export function normalizeWeekday(day: string | null | undefined): WeekdayKey | null {
  if (!day) return null;
  const key = day.trim().toLowerCase().slice(0, 3);
  return (WEEKDAY_KEYS as readonly string[]).includes(key) ? (key as WeekdayKey) : null;
}

/**
 * Property feeds often omit closed days. When any hours exist, return a
 * Monday–Sunday list and treat missing days as closed so every display
 * shows the full week.
 */
export function expandOfficeHoursToFullWeek<T extends OfficeHourEntry>(
  hours: T[],
): Array<T | OfficeHourEntry> {
  if (hours.length === 0) return [];

  const byDay = new Map<WeekdayKey, T>();
  const leftover: T[] = [];

  for (const entry of hours) {
    const key = normalizeWeekday(entry.day);
    if (!key) {
      leftover.push(entry);
      continue;
    }
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  const week = WEEKDAY_KEYS.map((key) => {
    const existing = byDay.get(key);
    if (existing) {
      return { ...existing, day: WEEKDAY_LABELS[key] };
    }
    return { day: WEEKDAY_LABELS[key], open_time: null, close_time: null };
  });

  return [...week, ...leftover];
}
